import React, { useEffect, useState } from "react";
import { Progress } from "antd";
import fleetIcon from "../../../../Assets/Icons/laptop_car.svg";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import LineChart from "../../../../Components/Charts/LineChart";
import StackedBarChart from "../../../../Components/Charts/StackedBarChart";
import BarChart from "../../../../Components/Charts/BarChart";
import GenericTable from "../../../../Components/Tables/GenericTable";
import totalFleetIcon from "../../../../Assets/Icons/laptop_car_fleet.svg";
import activeIcon from "../../../../Assets/Icons/delivery_truck_bolt.svg";
import creditIcon from "../../../../Assets/Icons/credit_card_utilized.svg";
import avgSpendIcon from "../../../../Assets/Icons/order_approve.svg";
import lockIcon from "../../../../Assets/Icons/lock.svg";
import { useDateFilter } from "../../../../Contexts/DateFilterContext";
import { getAdminReports } from "../../../../API/Admin/Reports";
import { getInitials } from "../../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const InitialsAvatar = ({ name }) => (
  <span
    style={{
      width: 24,
      height: 24,
      borderRadius: "5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      background: getAvatarColor(name),
      flexShrink: 0,
    }}
  >
    {getInitials(name)}
  </span>
);

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const FleetManagers = () => {
  const { from, to } = useDateFilter();
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getAdminReports("fleet-managers", startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          setReportData(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load fleet managers report:", err);
      }
    };

    fetchReport();
  }, [from, to]);

  const stats = reportData?.stats || {};
  const fuelSpendTrend = reportData?.fleet_fuel_spend_trend;
  const topFleetsBySpend = reportData?.top_fleet_managers_by_spend;
  const fuelTypeBreakdown = reportData?.fuel_type_breakdown;

  const vehicleDataSource = (reportData?.top_vehicles_by_fuel_usage || []).map(
    (vehicle, index) => ({
      key: String(vehicle.vehicle_id ?? index),
      vehicleId: vehicle.vehicle_id ?? "-",
      licensePlate: vehicle.license_plate || "-",
      driver: vehicle.driver_name || "-",
      fuelType: vehicle.fuel_type_name || "-",
      volume: vehicle.total_volume ? `${vehicle.total_volume} L` : "-",
      cost: formatCurrency(vehicle.total_amount),
      transactions: vehicle.transaction_count ?? 0,
      avgCost: formatCurrency(vehicle.avg_cost_per_liter),
    }),
  );

  const uniqueVehicleFuelTypes = [
    ...new Set(vehicleDataSource.map((item) => item.fuelType)),
  ];

  const vehicleTypeFilters = uniqueVehicleFuelTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const vehicleColumns = [
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "License Plate",
      dataIndex: "licensePlate",
      key: "licensePlate",
      enableSearch: true,
    },
    {
      title: "Driver Name",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      render: (type) => <span className="type-badge">{type}</span>,
      filters: vehicleTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
    },
    {
      title: "Fuel Volume",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat(a.volume.replace(/[^0-9.]/g, "")) || 0) -
        (parseFloat(b.volume.replace(/[^0-9.]/g, "")) || 0),
    },
    {
      title: "Fuel Cost",
      dataIndex: "cost",
      key: "cost",
      sorter: (a, b) =>
        (parseFloat(a.cost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.cost.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Transactions",
      dataIndex: "transactions",
      key: "transactions",
      sorter: (a, b) => (a.transactions || 0) - (b.transactions || 0),
    },
    {
      title: "Avg Cost/L",
      dataIndex: "avgCost",
      key: "avgCost",
      sorter: (a, b) =>
        (parseFloat(a.avgCost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.avgCost.replace(/[$,]/g, "")) || 0),
    },
  ];

  const fleetPerformanceDataSource = (reportData?.fleet_performance || []).map(
    (fleet, index) => ({
      key: String(fleet.fleet_company_id ?? index),
      vendor: fleet.fleet_company_name || "-",
      fleetId: fleet.fleet_company_id ?? "-",
      transactions: fleet.transaction_count ?? 0,
      type: Array.isArray(fleet.assigned_modules) ? fleet.assigned_modules : [],
      spend: formatCurrency(fleet.total_amount),
      usage: fleet.usage_percent ?? 0,
      status: capitalize(fleet.status) || "-",
    }),
  );

  const uniqueFleetTypes = [
    ...new Set(fleetPerformanceDataSource.flatMap((item) => item.type || [])),
  ];

  const fleetTypeFilters = uniqueFleetTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const statusFilters = [
    ...new Set(fleetPerformanceDataSource.map((item) => item.status)),
  ].map((status) => ({ text: status, value: status }));

  const fleetPerformanceColumns = [
    {
      title: "Fleet Manager",
      dataIndex: "vendor",
      key: "vendor",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InitialsAvatar name={text} />
          {text}
        </span>
      ),
      enableSearch: true,
    },
    {
      title: "Fleet ID",
      dataIndex: "fleetId",
      key: "fleetId",
    },
    {
      title: "Transactions",
      dataIndex: "transactions",
      key: "transactions",
      sorter: (a, b) => (a.transactions || 0) - (b.transactions || 0),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (types) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {types.length > 0
            ? types.map((t) => (
                <span key={t} className="type-badge">
                  {t}
                </span>
              ))
            : "-"}
        </div>
      ),
      filters: fleetTypeFilters,
      onFilter: (value, record) => record.type.includes(value),
    },
    {
      title: "Spend",
      dataIndex: "spend",
      key: "spend",
      sorter: (a, b) =>
        (parseFloat(a.spend.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.spend.replace(/[$,]/g, "")) || 0),
      render: (value) => <span className="spend-badge">{value}</span>,
    },
    {
      title: "Credit Usage",
      dataIndex: "usage",
      key: "usage",
      render: (percent) => {
        let color = "#16A34A";
        if (percent >= 80 && percent < 100) color = "#F59E0B";
        if (percent >= 100) color = "#DC2626";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              width: "160px",
            }}
          >
            <Progress
              percent={percent}
              strokeColor={color}
              showInfo={false}
              size="small"
            />
            <span
              style={{ fontSize: "12px", color: "#71717A", fontWeight: "500" }}
            >
              {percent}%
            </span>
          </div>
        );
      },
      sorter: (a, b) => (a.usage || 0) - (b.usage || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
  ];

  return (
    <div className="fleet-manager">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Fleets"
            value={stats.total_fleets?.value ?? stats.total_fleets ?? 0}
            subtitle="vs. last month"
            icon={totalFleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Active"
            value={stats.active_fleets?.value ?? stats.active_fleets ?? 0}
            subtitle="vs. last month"
            icon={activeIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(stats.total_spend?.value)}
            {...(stats.total_spend?.percentage != null && {
              percentage: `${stats.total_spend.percentage}`,
            })}
            isIncrease={stats.total_spend?.is_increase}
            subtitle="vs. last month"
            icon={fleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Credit Utilized"
            value={`${stats.credit_utilized_percent?.value ?? 0}%`}
            {...(stats.credit_utilized_percent?.percentage != null && {
              percentage: `${stats.credit_utilized_percent.percentage}`,
            })}
            isIncrease={stats.credit_utilized_percent?.is_increase}
            subtitle="vs. last month"
            icon={creditIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Avg Spend"
            value={formatCurrency(stats.avg_spend?.value ?? stats.avg_spend)}
            subtitle="vs. last month"
            icon={avgSpendIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Locked Count"
            value={stats.locked_count?.value ?? stats.locked_count ?? 0}
            subtitle="Overall Locked"
            icon={lockIcon}
          />
        </div>
      </div>
      <div className="row">
        <div className="graph">
          <LineChart
            graphData={{
              labels: fuelSpendTrend?.labels || [],
              Spend: (fuelSpendTrend?.spend || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Volume: (fuelSpendTrend?.volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#F59E0B", "#16A34A"]}
            graphTitle="Fleet Fuel Spend"
            selectTenByDefault={false}
            units={["$", "L"]}
            classNameCSS="fuel-spend"
          />
        </div>
        <div className="graph">
          <StackedBarChart
            graphData={{
              labels: topFleetsBySpend?.labels || [],
              Volume: (topFleetsBySpend?.total_volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Amount: (topFleetsBySpend?.total_amount || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#4F46E5", "#F59E0B"]}
            graphTitle="Top Fleet Managers by Spend"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="top-fleets"
          />
        </div>
      </div>
      <div className="row">
        <div className="graph fuel-bar-graph-volume">
          <BarChart
            graphData={{
              labels: fuelTypeBreakdown?.labels || [],
              volume: (fuelTypeBreakdown?.total_volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={1}
            colourTheme={["#EF4444"]}
            graphTitle="Volume by Fuel Type"
            selectTenByDefault={false}
            units={["L"]}
            classNameCSS="fuel-vol"
            grayzoned={true}
          />
        </div>
      </div>
      <div className="row">
        <div className="graph spend-graph">
          <BarChart
            graphData={{
              labels: fuelTypeBreakdown?.labels || [],
              Fuel_Spend: (fuelTypeBreakdown?.total_amount || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={1}
            colourTheme={["#16A34A"]}
            graphTitle="Spend by Fuel Type"
            selectTenByDefault={false}
            units={["$"]}
            classNameCSS="quantity-to-use"
            grayzoned={true}
          />
        </div>
      </div>
      <div className="vehicle-table">
        <GenericTable
          title="Top Vehicle by Fuel Usage"
          dataSource={vehicleDataSource}
          columns={vehicleColumns}
        />
      </div>
      <div className="performance-table">
        <GenericTable
          title="Fleet Performance"
          dataSource={fleetPerformanceDataSource}
          columns={fleetPerformanceColumns}
        />
      </div>
    </div>
  );
};

export default FleetManagers;
